import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DoctorCard, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { getConfig } from '../../config.ts';
import { ROUTES } from '../../routes.ts';
import { TOS_VERSION, ALLOWED_IMAGE_TYPES } from '@dermalink/shared';
import type {
  VerifyStatusResponse,
  VerifyResponse,
  RizivDoctor,
  RizivSearchResponse,
  RizivAttestResponse,
  RizivConfirmRequest,
  RizivConfirmResponse,
  TosAcceptRequest,
  TosAcceptResponse,
  DoctorListItem,
} from '@dermalink/shared';
import styles from './Verification.module.css';

const MAX_ATTEMPTS  = 3;
const SUPPORT_EMAIL = 'verificatie@dermalink.be';

// Phone/tablet detection — drives camera vs file-upload UI
const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

type Step =
  | 'loading'
  | 'already_verified'
  | 'locked'
  | 'front'
  | 'front_preview'
  | 'back'
  | 'back_preview'
  | 'processing'
  | 'success'
  | 'failure';

type RizivStep = 'idle' | 'searching' | 'results' | 'error' | 'confirmed';

export function Verification() {
  const navigate  = useNavigate();
  const authFetch = useAuthFetch();

  // ── Identity card state ────────────────────────────────────────
  const [step,         setStep]         = useState<Step>('loading');
  const [attempts,     setAttempts]     = useState(0);
  const [frontDataUrl, setFrontDataUrl] = useState<string | null>(null);
  const [backDataUrl,  setBackDataUrl]  = useState<string | null>(null);
  const [errors,       setErrors]       = useState<string[]>([]);
  const [cameraError,  setCameraError]  = useState<string | null>(null);

  // ── Professional (RIZIV) state ─────────────────────────────────
  const rizivSearchFired               = useRef(false);
  const [rizivStep,    setRizivStep]    = useState<RizivStep>('idle');
  const [rizivResults, setRizivResults] = useState<RizivDoctor[]>([]);
  const [rizivError,   setRizivError]   = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<RizivDoctor | null>(null);
  const [confirming,   setConfirming]   = useState(false);
  const [legalAttested, setLegalAttested] = useState(false);

  // ── Professional verified flag + doctor name for RIZIV lookup ─
  const [professionVerified, setProfessionVerified] = useState(false);
  const [doctorName, setDoctorName] = useState<{ firstName: string; lastName: string } | null>(null);
  const [confirmedRizivNumber,   setConfirmedRizivNumber]   = useState<string | null>(null);
  const [confirmedQualification, setConfirmedQualification] = useState<string | null>(null);

  // ── TOS acceptance state ───────────────────────────────────────
  const [tosAccepted,    setTosAccepted]    = useState(false);
  const [tosSubmitting,  setTosSubmitting]  = useState(false);
  const [tosError,       setTosError]       = useState<string | null>(null);

  // Camera refs — only used on mobile
  const videoRef  = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ── Load current verification status ──────────────────────────
  useEffect(() => {
    async function load() {
      try {
        const res = await authFetch('/doctor/verify-status');
        if (!res.ok) { setStep('front'); return; }
        const data: VerifyStatusResponse = await res.json();
        setAttempts(data.attempts);
        setProfessionVerified(data.profession_verified);
        setTosAccepted(data.tos_accepted);
        if (data.first_name && data.last_name) {
          setDoctorName({ firstName: data.first_name, lastName: data.last_name });
        }
        setConfirmedRizivNumber(data.riziv_number ?? null);
        setConfirmedQualification(data.riziv_qualification ?? null);
        if (data.identity_verified) {
          setStep('already_verified');
        } else if (data.attempts >= MAX_ATTEMPTS) {
          setStep('locked');
        } else {
          setStep('front');
        }
      } catch {
        setStep('front');
      }
    }
    load();
  }, [authFetch]);

  // ── Camera lifecycle (mobile only) ────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:      { ideal: 1920 },
          height:     { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      setCameraError('Camera kon niet worden gestart. Geef toegang tot de camera en probeer opnieuw.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (step === 'front' || step === 'back') {
      startCamera();
    }
    return () => stopCamera();
  }, [step, startCamera, stopCamera]);

  // ── Camera capture ────────────────────────────────────────────
  function captureFrame(): string | null {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  function handleCaptureFront() {
    const dataUrl = captureFrame();
    if (!dataUrl) return;
    setFrontDataUrl(dataUrl);
    setStep('front_preview');
  }

  function handleCaptureBack() {
    const dataUrl = captureFrame();
    if (!dataUrl) return;
    setBackDataUrl(dataUrl);
    setStep('back_preview');
  }

  // ── File upload (desktop) — preview is handled inside UploadStep ──
  function handleSelectFront(dataUrl: string) {
    setFrontDataUrl(dataUrl);
    setStep('back');
  }

  async function handleSelectBack(dataUrl: string) {
    setBackDataUrl(dataUrl);
    await handleSubmit(frontDataUrl ?? null, dataUrl);
  }

  // ── Submit identity card images ────────────────────────────────
  // Accepts explicit data URLs (desktop flow) or reads from state (mobile flow).
  async function handleSubmit(frontUrl = frontDataUrl, backUrl = backDataUrl) {
    if (!frontUrl || !backUrl) return;
    setStep('processing');
    setErrors([]);

    const frontB64 = frontUrl.split(',')[1];
    const backB64  = backUrl.split(',')[1];

    try {
      const res = await authFetch('/doctor/verify', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ front: frontB64, back: backB64 }),
      });

      if (res.status === 429) {
        setAttempts(MAX_ATTEMPTS);
        setStep('locked');
        return;
      }

      const data: VerifyResponse = await res.json();

      if (data.success) {
        setStep('success');
      } else {
        setAttempts((a) => a + 1);
        setErrors(data.errors ?? ['Verificatie mislukt. Probeer opnieuw.']);
        setStep('failure');
      }
    } catch {
      setErrors(['Er is een verbindingsfout opgetreden. Probeer het opnieuw.']);
      setStep('failure');
    }
  }

  function handleRetry() {
    setFrontDataUrl(null);
    setBackDataUrl(null);
    setErrors([]);
    setStep('front');
  }

  // ── RIZIV search ───────────────────────────────────────────────
  // In staging/prod: calls the RIZIV Lambda Function URL directly (no VPC needed).
  // In local dev:    falls back to the authenticated backend route.
  const handleRizivSearch = useCallback(async function handleRizivSearch() {
    setRizivStep('searching');
    setRizivError(null);
    setRizivResults([]);
    try {
      let res: Response;
      const rizivUrl = getConfig().riziv_lookup_url;
      if (rizivUrl && doctorName) {
        const url = new URL(rizivUrl);
        url.searchParams.set('firstName', doctorName.firstName);
        url.searchParams.set('lastName',  doctorName.lastName);
        res = await fetch(url.toString());
      } else {
        // Local dev fallback — backend proxies RIZIV directly
        res = await authFetch('/doctor/riziv-search');
      }
      if (!res.ok) {
        setRizivError('De RIZIV-zoekopdracht is mislukt. Probeer het later opnieuw.');
        setRizivStep('error');
        return;
      }
      const data: RizivSearchResponse = await res.json();
      setRizivResults(data.results);
      setRizivStep('results');
    } catch {
      setRizivError('Er is een verbindingsfout opgetreden. Probeer het later opnieuw.');
      setRizivStep('error');
    }
  }, [authFetch, doctorName]);

  // ── Auto-trigger RIZIV search once identity is verified ───────
  useEffect(() => {
    if (professionVerified || rizivSearchFired.current) return;
    if (step === 'success' || step === 'already_verified') {
      rizivSearchFired.current = true;
      handleRizivSearch();
    }
  }, [step, professionVerified, handleRizivSearch]);

  // ── RIZIV confirm ──────────────────────────────────────────────
  function handlePickRiziv(rizivNumber: string) {
    const doctor = rizivResults.find((r) => r.riziv_number === rizivNumber) ?? null;
    setPendingConfirm(doctor);
  }

  async function handleConfirmRiziv() {
    if (!pendingConfirm || !doctorName) return;
    setConfirming(true);
    try {
      // ── Step 1: get an attestation token from the RIZIV Lambda ──
      // The RIZIV Lambda re-scrapes to confirm the number is real,
      // then returns a short-lived signed token. Skipped in local dev.
      let attestToken: string | null = null;
      const rizivUrl = getConfig().riziv_lookup_url;
      if (rizivUrl) {
        // Append /attest as a relative segment (not an absolute path) so the
        // /riziv prefix from the CloudFront route is preserved.
        const base = rizivUrl.replace(/\/+$/, '');
        const attestUrl = new URL(`${base}/attest`);
        attestUrl.searchParams.set('rizivNumber', pendingConfirm.riziv_number);
        attestUrl.searchParams.set('firstName',   doctorName.firstName);
        attestUrl.searchParams.set('lastName',    doctorName.lastName);
        const attestRes = await fetch(attestUrl.toString());
        if (!attestRes.ok) {
          setRizivError('Verificatie bij het RIZIV is mislukt. Probeer het opnieuw.');
          setRizivStep('error');
          setPendingConfirm(null);
          return;
        }
        const attestData: RizivAttestResponse = await attestRes.json();
        attestToken = attestData.token;
      }

      // ── Step 2: persist the confirmed selection ──────────────────
      const body: RizivConfirmRequest = {
        riziv_number:  pendingConfirm.riziv_number,
        qualification: pendingConfirm.qualification,
        token:         attestToken,
      };
      const res = await authFetch('/doctor/riziv-confirm', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data: RizivConfirmResponse = await res.json();
      if (data.success) {
        setProfessionVerified(true);
        setRizivStep('confirmed');
        setConfirmedRizivNumber(pendingConfirm.riziv_number);
        setConfirmedQualification(pendingConfirm.qualification ?? null);
        setPendingConfirm(null);
        setLegalAttested(false);
      } else {
        setRizivError('Bevestiging mislukt. Probeer het opnieuw.');
        setRizivStep('error');
      }
    } catch {
      setRizivError('Er is een verbindingsfout opgetreden. Probeer het opnieuw.');
      setRizivStep('error');
    } finally {
      setConfirming(false);
    }
  }

  // ── TOS accept ────────────────────────────────────────────────
  async function handleTosAccept() {
    setTosSubmitting(true);
    setTosError(null);
    try {
      const req: TosAcceptRequest = { version: TOS_VERSION };
      const res = await authFetch('/doctor/tos-accept', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(req),
      });
      const data: TosAcceptResponse = await res.json();
      if (data.success) {
        setTosAccepted(true);
      } else {
        setTosError('Aanvaarding mislukt. Probeer het opnieuw.');
      }
    } catch {
      setTosError('Er is een verbindingsfout opgetreden. Probeer het opnieuw.');
    } finally {
      setTosSubmitting(false);
    }
  }

  const attemptsLeft     = MAX_ATTEMPTS - attempts;
  const identityVerified = step === 'success' || step === 'already_verified';
  const professionDone   = professionVerified;

  // ── Render ─────────────────────────────────────────────────────
  return (
    <>
      <GlobalStepper identityDone={identityVerified} professionDone={professionDone} tosAccepted={tosAccepted} />

      {step === 'loading' ? (
        <p className="text-muted status-message">Laden&hellip;</p>

      ) : !identityVerified ? (
        /* ── Step 1: Identity card ── */
        <section className={styles.section}>
          {step === 'locked' && <LockedMessage email={SUPPORT_EMAIL} />}

          {(step === 'front' || step === 'back') && (
            isMobile ? (
              <CaptureStep
                side={step}
                videoRef={videoRef}
                canvasRef={canvasRef}
                cameraError={cameraError}
                attemptsLeft={attemptsLeft}
                onCapture={step === 'front' ? handleCaptureFront : handleCaptureBack}
                onRetryCamera={startCamera}
              />
            ) : (
              <UploadStep
                key={step}
                side={step}
                attemptsLeft={attemptsLeft}
                onSelect={step === 'front' ? handleSelectFront : handleSelectBack}
                acceptLabel={step === 'back' ? 'Versturen' : 'Volgende'}
                onCancel={() => navigate(ROUTES.doctorDashboard)}
              />
            )
          )}

          {/* Mobile-only preview steps */}
          {step === 'front_preview' && frontDataUrl && (
            <PreviewStep
              label="Voorkant"
              dataUrl={frontDataUrl}
              retakeLabel="Opnieuw nemen"
              onAccept={() => setStep('back')}
              onRetake={() => setStep('front')}
            />
          )}
          {step === 'back_preview' && backDataUrl && (
            <PreviewStep
              label="Achterkant"
              dataUrl={backDataUrl}
              retakeLabel="Opnieuw nemen"
              onAccept={() => handleSubmit()}
              onRetake={() => setStep('back')}
              acceptLabel="Versturen"
            />
          )}

          {step === 'processing' && (
            <div className={styles.processingState}>
              <div className={styles.spinner} aria-hidden="true" />
              <p className="text-muted">Uw identiteitskaart wordt geanalyseerd&hellip;</p>
            </div>
          )}

          {step === 'failure' && (
            <FailureStep
              errors={errors}
              attemptsLeft={attemptsLeft}
              onRetry={attemptsLeft > 0 ? handleRetry : undefined}
              email={SUPPORT_EMAIL}
            />
          )}
        </section>

      ) : !professionDone ? (
        /* ── Step 2: RIZIV professional verification ── */
        <section className={styles.section}>
          <div className={styles.rizivSection}>
            <p className={`${styles.captureHint} ${styles.rizivIntroHint}`}>
              We zoeken uw naam op in het RIZIV-register. Selecteer uw profiel en bevestig —
              deze keuze kan nadien niet meer worden gewijzigd.
            </p>

            {rizivStep === 'searching' && (
              <div className={styles.processingState}>
                <div className={styles.spinner} aria-hidden="true" />
                <p className="text-muted">Opzoeken in het RIZIV-register&hellip;</p>
              </div>
            )}

            {rizivStep === 'error' && (
              <div className={styles.failureContainer}>
                <div className={styles.bannerError} role="alert">{rizivError}</div>
                <Button variant="primary" onClick={handleRizivSearch}>
                  Opnieuw proberen
                </Button>
              </div>
            )}

            {rizivStep === 'results' && (
              <div className={styles.rizivResults}>
                {rizivResults.length === 0 ? (
                  <div className={styles.bannerWarning} role="status">
                    Geen artsen gevonden voor uw naam in het RIZIV-register.
                    Controleer of uw naam correct is ingevuld in uw profiel.
                  </div>
                ) : (
                  <>
                    <p className={styles.captureHint}>
                      {rizivResults.length === 1
                        ? 'Er is 1 resultaat gevonden. Klopt dit profiel?'
                        : `Er zijn ${rizivResults.length} resultaten gevonden. Selecteer uw profiel.`}
                    </p>
                    <div className={styles.rizivCardList}>
                      {rizivResults.map((r) => (
                        <DoctorCard
                          key={r.riziv_number}
                          doctor={rizivDoctorToListItem(r)}
                          onSelect={handlePickRiziv}
                          selectKey="riziv_number"
                          buttonLabel="Dit ben ik"
                        />
                      ))}
                    </div>
                  </>
                )}
                <Button variant="ghost" className={styles.rizivRetryBtn} onClick={handleRizivSearch}>
                  Opnieuw zoeken
                </Button>
              </div>
            )}
          </div>
        </section>

      ) : !tosAccepted ? (
        /* ── Step 3: Usage agreement ── */
        <TermsStep
          submitting={tosSubmitting}
          error={tosError}
          onAccept={handleTosAccept}
        />

      ) : (
        /* ── Step 4: Completion summary ── */
        <CompletionStep
          doctorName={doctorName}
          rizivNumber={confirmedRizivNumber}
          qualification={confirmedQualification}
          onDashboard={() => navigate(ROUTES.doctorDashboard)}
        />
      )}

      {/* Confirmation dialog — rendered outside sections so it overlays everything */}
      {pendingConfirm && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
          <div className={styles.confirmBox}>
            <h3 className={styles.confirmTitle}>Profiel bevestigen</h3>
            <p>
              U staat op het punt het volgende RIZIV-profiel te koppelen aan uw account.
              <strong> Deze keuze kan niet worden gewijzigd.</strong>
            </p>
            <div className={styles.confirmCard}>
              <p><strong>{pendingConfirm.name}</strong></p>
              <p>RIZIV: {pendingConfirm.riziv_number}</p>
              {pendingConfirm.qualification && <p>{pendingConfirm.qualification}</p>}
            </div>
            <div className={styles.legalAttestation}>
              <p className={styles.legalText}>
                Door dit profiel te bevestigen verklaart u uitdrukkelijk en onder eer en geweten
                dat u de houder bent van het bovenstaande RIZIV-nummer en dat u gerechtigd bent
                de geneeskunde uit te oefenen in België. Een valse verklaring, het
                frauduleus gebruik van het RIZIV-nummer van een derde, of het onrechtmatig
                uitoefenen van een gereglementeerd gezondheidszorgberoep is strafbaar onder het
                Belgische recht en kan aanleiding geven tot strafrechtelijke vervolging wegens
                identiteitsmisbruik en/of medische fraude, alsook tot burgerrechtelijke
                aansprakelijkheid.
              </p>
              <label className={styles.legalCheckbox}>
                <input
                  type="checkbox"
                  checked={legalAttested}
                  onChange={(e) => setLegalAttested(e.target.checked)}
                  disabled={confirming}
                />
                <span>
                  Ik bevestig dat ik de houder ben van dit RIZIV-nummer en aanvaard
                  de bovenstaande verklaring.
                </span>
              </label>
            </div>
            <div className={styles.confirmActions}>
              <Button
                variant="ghost"
                onClick={() => { setPendingConfirm(null); setLegalAttested(false); }}
                disabled={confirming}
              >
                Annuleren
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmRiziv}
                disabled={confirming || !legalAttested}
              >
                {confirming ? 'Bezig…' : 'Bevestigen'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────

/** Maps a RIZIV search result to the DoctorListItem shape used by DoctorCard. */
function rizivDoctorToListItem(r: RizivDoctor): DoctorListItem {
  // Name format from silverpages: "JANSSENS, JAN"
  const [rawLast = '', rawFirst = ''] = r.name.split(',').map((s) => s.trim());
  const lastName  = capitalize(rawLast);
  const firstName = capitalize(rawFirst);

  return {
    cognito_sub:        r.riziv_number, // used as a unique key in this context
    first_name:         firstName || null,
    last_name:          lastName  || null,
    riziv_number:       r.riziv_number,
    riziv_qualification: r.qualification,
    organization_name:  null,
    organization_slug:  null,
    picture_url:        null,
    is_available:       true,
  };
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function attemptsText(n: number): string {
  return `Nog ${n} poging${n !== 1 ? 'en' : ''} over.`;
}

// ── Sub-components ─────────────────────────────────────────────

type StepState = 'active' | 'done' | 'pending';

function circleClass(state: StepState) {
  if (state === 'active') return `${styles.stepperCircle} ${styles.stepperCircleActive}`;
  if (state === 'done')   return `${styles.stepperCircle} ${styles.stepperCircleDone}`;
  return styles.stepperCircle;
}

function labelClass(state: StepState) {
  return state === 'active' || state === 'done'
    ? `${styles.stepperLabel} ${styles.stepperLabelActive}`
    : styles.stepperLabel;
}

interface GlobalStepperProps {
  identityDone:   boolean;
  professionDone: boolean;
  tosAccepted:    boolean;
}

function GlobalStepper({ identityDone, professionDone, tosAccepted }: GlobalStepperProps) {
  const s1: StepState = identityDone   ? 'done'   : 'active';
  const s2: StepState = identityDone   ? (professionDone ? 'done' : 'active') : 'pending';
  const s3: StepState = professionDone ? (tosAccepted ? 'done' : 'active') : 'pending';
  const s4: StepState = tosAccepted    ? 'done'   : 'pending';

  return (
    <div className={styles.globalStepper} aria-label="Verificatiestappen">
      {/* Step 1 */}
      <div className={styles.stepperItem}>
        <div className={styles.stepperConnector} aria-hidden="true" />
        <div className={circleClass(s1)} aria-current={s1 === 'active' ? 'step' : undefined}>
          {s1 === 'done' ? <CheckIcon /> : '1'}
        </div>
        <span className={labelClass(s1)}>Identiteitsbewijs</span>
      </div>

      {/* Step 2 */}
      <div className={styles.stepperItem}>
        <div className={styles.stepperConnector} aria-hidden="true" />
        <div className={circleClass(s2)} aria-current={s2 === 'active' ? 'step' : undefined}>
          {s2 === 'done' ? <CheckIcon /> : '2'}
        </div>
        <span className={labelClass(s2)}>Professionele hoedanigheid</span>
      </div>

      {/* Step 3 */}
      <div className={styles.stepperItem}>
        <div className={styles.stepperConnector} aria-hidden="true" />
        <div className={circleClass(s3)} aria-current={s3 === 'active' ? 'step' : undefined}>
          {s3 === 'done' ? <CheckIcon /> : '3'}
        </div>
        <span className={labelClass(s3)}>Gebruiksovereenkomst</span>
      </div>

      {/* Step 4 — no connector after last item */}
      <div className={styles.stepperItem}>
        <div className={circleClass(s4)}>
          {s4 === 'done' ? <CheckIcon /> : '4'}
        </div>
        <span className={labelClass(s4)}>Voltooiing</span>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      width="16" height="16" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
      width="13" height="13" aria-hidden="true">
      <path d="M12 1a5 5 0 0 0-5 5v3H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2h-2V6a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3zm0 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      width="28" height="28" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

const ID_TIPS = [
  { title: 'Goede belichting',      text: 'Vermijd reflecties van flitsers of direct zonlicht op de kaart.' },
  { title: 'Alle hoeken zichtbaar', text: 'Zorg dat het volledige document in beeld past.' },
  { title: 'Tekst leesbaar',        text: 'Wazige of onscherpe foto\'s worden automatisch afgewezen.' },
] as const;

function TipsPanel({ side }: { side: 'front' | 'back' }) {
  const isFront = side === 'front';

  const title = isFront
    ? 'Verifieer uw identiteit'
    : 'Achterkant identiteitskaart';

  const body = isFront
    ? 'Upload een duidelijke foto van de voorzijde van uw identiteitskaart. Zorg voor goede belichting en scherpte.'
    : 'Upload nu een foto van de achterkant van uw identiteitskaart. Zorg voor goede belichting en scherpte.';

  return (
    <div className={styles.stepInfoPanel}>
      <div>
        <h1 className={styles.stepInfoTitle}>{title}</h1>
        <p className={styles.stepInfoBody}>{body}</p>
      </div>
      <div className={styles.tipsList}>
        {ID_TIPS.map((tip) => (
          <div key={tip.title} className={styles.tipRow}>
            <span className={styles.tipIcon}>
              <CheckIcon />
            </span>
            <div className={styles.tipContent}>
              <p className={styles.tipTitle}>{tip.title}</p>
              <p className={styles.tipText}>{tip.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CaptureStepProps {
  side:          'front' | 'back';
  videoRef:      React.RefObject<HTMLVideoElement | null>;
  canvasRef:     React.RefObject<HTMLCanvasElement | null>;
  cameraError:   string | null;
  attemptsLeft:  number;
  onCapture:     () => void;
  onRetryCamera: () => void;
}

function CaptureStep({
  side, videoRef, canvasRef, cameraError, attemptsLeft, onCapture, onRetryCamera,
}: CaptureStepProps) {
  const isFront = side === 'front';
  return (
    <div className={styles.captureContainer}>
      <StepIndicator side={side} />

      <h2 className={styles.captureTitle}>
        {isFront ? 'Stap 1 — Voorkant' : 'Stap 2 — Achterkant'}
      </h2>
      <p className={styles.captureHint}>
        {isFront
          ? 'Hou de voorkant van uw identiteitskaart voor de camera. Zorg dat de naam duidelijk leesbaar is.'
          : 'Hou nu de achterkant voor de camera. Zorg dat de MRZ-streep onderaan volledig zichtbaar is.'}
      </p>

      <AttemptsWarning attemptsLeft={attemptsLeft} />

      {cameraError ? (
        <div className={styles.bannerError} role="alert">
          {cameraError}
          <Button variant="ghost" className={styles.retryBtn} onClick={onRetryCamera}>
            Opnieuw proberen
          </Button>
        </div>
      ) : (
        <div className={styles.cameraWrapper}>
          <video ref={videoRef} className={styles.video} autoPlay playsInline muted />
          <div className={styles.cardOverlay} aria-hidden="true">
            <div className={styles.cardGuide} />
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className={styles.hiddenCanvas} />

      <Button variant="primary" onClick={onCapture} disabled={!!cameraError}>
        Foto nemen
      </Button>
    </div>
  );
}

interface UploadStepProps {
  side:          'front' | 'back';
  attemptsLeft:  number;
  onSelect:      (dataUrl: string) => void;
  onCancel:      () => void;
  acceptLabel?:  string;
}

function UploadStep({ side, attemptsLeft, onSelect, onCancel, acceptLabel = 'Volgende' }: UploadStepProps) {
  const isFront    = side === 'front';
  const inputRef   = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  function readFile(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot (max. 10 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) readFile(file);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) readFile(file);
  }

  return (
    <div className={styles.uploadLayout}>
      {/* Left: contextual instructions */}
      <TipsPanel side={side} />

      {/* Right: upload card */}
      <div className={styles.uploadCardColumn}>
        <div className={styles.uploadCard}>
          <AttemptsWarning attemptsLeft={attemptsLeft} />

          {/* Drop zone — shows image preview when a file has been selected */}
          <div
            className={`${styles.dropZoneEnhanced}${previewUrl ? ` ${styles.dropZonePreviewMode}` : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
            aria-label={previewUrl ? 'Klik om een ander bestand te kiezen' : undefined}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Preview identiteitskaart" className={styles.dropZonePreviewImg} />
            ) : (
              <>
                <div className={styles.dropZoneIconCircle} aria-hidden="true">
                  <UploadIcon />
                </div>
                <p className={styles.dropZoneHeading}>
                  {isFront ? 'Upload voorzijde identiteitskaart' : 'Upload achterzijde identiteitskaart'}
                </p>
                <p className={styles.dropZoneSubtext}>
                  Sleep uw bestand hierheen of{' '}
                  <span className={styles.dropZoneLink}>klik om te bladeren</span>
                </p>
                <div className={styles.fileChips}>
                  {ALLOWED_IMAGE_TYPES.map((mime) => (
                    <span key={mime} className={styles.fileChip}>{mime.split('/')[1].toUpperCase()}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            className={styles.hiddenInput}
            onChange={handleChange}
          />

          {/* Action row */}
          <div className={styles.uploadActions}>
            {previewUrl ? (
              <>
                <Button variant="ghost" onClick={() => setPreviewUrl(null)}>
                  Ander bestand kiezen
                </Button>
                <Button variant="primary" onClick={() => onSelect(previewUrl)}>
                  {acceptLabel}
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={onCancel}>
                Annuleren
              </Button>
            )}
          </div>
        </div>

        {/* Privacy note */}
        <p className={styles.privacyNote}>
          <span className={styles.privacyNoteIcon}><LockIcon /></span>
          Uw gegevens worden veilig versleuteld en uitsluitend gebruikt voor identiteitscontrole conform AVG.
        </p>
      </div>
    </div>
  );
}

interface PreviewStepProps {
  label:        string;
  dataUrl:      string;
  retakeLabel:  string;
  onAccept:     () => void;
  onRetake:     () => void;
  acceptLabel?: string;
}

function PreviewStep({ label, dataUrl, retakeLabel, onAccept, onRetake, acceptLabel = 'Volgende' }: PreviewStepProps) {
  return (
    <div className={styles.previewContainer}>
      <h2 className={styles.captureTitle}>{label} — Controleer de afbeelding</h2>
      <p className={styles.captureHint}>
        Is de kaart scherp en volledig zichtbaar?
      </p>
      <img src={dataUrl} alt={`Preview ${label}`} className={styles.previewImage} />
      <div className={styles.previewActions}>
        <Button variant="ghost" onClick={onRetake}>{retakeLabel}</Button>
        <Button variant="primary" onClick={onAccept}>{acceptLabel}</Button>
      </div>
    </div>
  );
}

interface FailureStepProps {
  errors:       string[];
  attemptsLeft: number;
  onRetry?:     () => void;
  email:        string;
}

function FailureStep({ errors, attemptsLeft, onRetry, email }: FailureStepProps) {
  return (
    <div className={styles.failureContainer}>
      <div className={styles.bannerError} role="alert">
        <strong>Verificatie mislukt.</strong>
        <ul className={styles.errorList}>
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      </div>
      {onRetry ? (
        <>
          <p className={`text-muted ${styles.attemptsWarning}`}>
            {attemptsText(attemptsLeft)}
          </p>
          <Button variant="primary" onClick={onRetry}>Opnieuw proberen</Button>
        </>
      ) : (
        <LockedMessage email={email} />
      )}
    </div>
  );
}

function LockedMessage({ email }: { email: string }) {
  return (
    <div className={styles.lockedContainer}>
      <div className={styles.bannerWarning} role="alert">
        <strong>Maximaal aantal pogingen bereikt.</strong>
        <p>
          Stuur een e-mail met foto&apos;s van voor- en achterkant van uw
          identiteitskaart naar <a href={`mailto:${email}`}>{email}</a>.
        </p>
      </div>
    </div>
  );
}

function StepIndicator({ side }: { side: 'front' | 'back' }) {
  const isFront = side === 'front';
  return (
    <div className={styles.stepIndicator}>
      <span className={`${styles.stepDot} ${isFront ? styles.stepDotActive : styles.stepDotDone}`} />
      <span className={styles.stepLine} />
      <span className={`${styles.stepDot} ${!isFront ? styles.stepDotActive : ''}`} />
    </div>
  );
}

function AttemptsWarning({ attemptsLeft }: { attemptsLeft: number }) {
  if (attemptsLeft >= MAX_ATTEMPTS) return null;
  return (
    <p className={`${styles.attemptsWarning} text-muted`}>
      {attemptsText(attemptsLeft)}
    </p>
  );
}

interface TermsStepProps {
  submitting: boolean;
  error:      string | null;
  onAccept:   () => void;
}

function TermsStep({ submitting, error, onAccept }: TermsStepProps) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className={styles.tosContainer}>
      <h2 className={styles.tosTitle}>Gebruiksovereenkomst voor zorgverleners</h2>
      <p className={styles.tosIntro}>
        Lees de onderstaande overeenkomst zorgvuldig door en aanvaard deze om uw registratie te voltooien.
      </p>

      <div className={styles.tosBody}>
        <p><strong>Dermalink — Communicatieplatform voor Teledermatologie</strong></p>

        <p><strong>Partijen</strong><br />
        Deze overeenkomst wordt gesloten tussen:<br />
        Dermalink BV, hierna "het Platform", en<br />
        de geregistreerde zorgverlener die deze overeenkomst elektronisch aanvaardt, hierna "de Zorgverlener".</p>

        <p><strong>Artikel 1 — Aard van het platform</strong><br />
        1.1 Dermalink is uitsluitend een digitaal communicatieplatform. Het Platform faciliteert de uitwisseling
        van medische informatie en beeldmateriaal tussen patiënten en zorgverleners, maar verleent zelf geen
        medische zorg en oefent geen medische activiteit uit in de zin van de wet op de uitoefening van de
        gezondheidszorgberoepen (KB nr. 78).<br />
        1.2 Het Platform treedt op geen enkel moment op als zorgverlener, behandelaar, of medisch adviseur.
        Alle medische handelingen, diagnoses, adviezen en behandelbeslissingen vallen uitsluitend onder de
        professionele verantwoordelijkheid van de Zorgverlener.</p>

        <p><strong>Artikel 2 — Verantwoordelijkheid van de Zorgverlener</strong><br />
        2.1 De Zorgverlener bevestigt dat de arts-patiëntrelatie, inclusief alle daaruit voortvloeiende rechten
        en plichten, uitsluitend bestaat tussen de Zorgverlener en de patiënt. Het Platform is geen partij in
        deze relatie.<br />
        2.2 De Zorgverlener is als enige verantwoordelijk voor:</p>
        <ul>
          <li>de kwaliteit en correctheid van verstrekte medische adviezen;</li>
          <li>het beoordelen of de aangeleverde informatie en beeldmateriaal voldoende zijn voor een verantwoorde beoordeling;</li>
          <li>de beslissing om al dan niet een consult te aanvaarden of door te verwijzen;</li>
          <li>naleving van alle toepasselijke deontologische regels, inclusief de code van de Orde der Artsen.</li>
        </ul>
        <p>2.3 De Zorgverlener erkent dat teleconsultatie via het Platform geen vervanging vormt van een fysiek
        onderzoek waar de klinische situatie dit vereist, en verbindt zich ertoe patiënten actief door te
        verwijzen wanneer dit medisch noodzakelijk is.</p>

        <p><strong>Artikel 3 — Identiteit en beroepskwalificaties</strong><br />
        3.1 De Zorgverlener bevestigt dat hij/zij beschikt over een geldig RIZIV-nummer en gerechtigd is tot
        de zelfstandige uitoefening van de geneeskunde in België.<br />
        3.2 De Zorgverlener verbindt zich ertoe het Platform onmiddellijk te informeren bij verlies, schorsing
        of beperking van het recht tot uitoefening van het beroep.<br />
        3.3 De door de Zorgverlener verstrekte identiteits- en kwalificatiegegevens zijn naar zijn/haar beste
        weten volledig en waarheidsgetrouw. Het Platform mag op deze gegevens vertrouwen zonder verdere
        verificatieplicht, onverminderd de verificatie die het Platform redelijkerwijs uitvoert bij registratie.</p>

        <p><strong>Artikel 4 — Gegevensverwerking</strong><br />
        4.1 De Zorgverlener erkent dat medische gegevens van patiënten worden verwerkt conform de AVG en de
        wet van 30 juli 2018 betreffende de bescherming van natuurlijke personen met betrekking tot de
        verwerking van persoonsgegevens.<br />
        4.2 De Zorgverlener treedt op als verwerkingsverantwoordelijke voor de medische dossiervorming. Het
        Platform treedt op als verwerker in de zin van artikel 28 AVG voor zover het technische verwerking
        verricht ten behoeve van de Zorgverlener, conform de afzonderlijke verwerkersovereenkomst.<br />
        4.3 De Zorgverlener erkent dat de informatieverplichting jegens de patiënt inzake gegevensverwerking
        wordt vervuld door het Platform via zijn privacyverklaring bij registratie. De Zorgverlener onthoudt
        zich van verwerkingen buiten het Platform die niet gedekt zijn door die informatieverplichting, tenzij
        hij/zij daarvoor een zelfstandige wettelijke grondslag kan aantonen.</p>

        <p><strong>Artikel 5 — Aansprakelijkheidsbeperking</strong><br />
        5.1 Het Platform is niet aansprakelijk voor schade voortvloeiend uit medische beslissingen, adviezen
        of handelingen van de Zorgverlener.<br />
        5.2 Het Platform is niet aansprakelijk voor schade die voortvloeit uit onvoldoende of onjuist
        aangeleverd beeldmateriaal of informatie door de patiënt, voor zover het Platform redelijke maatregelen
        heeft genomen om een kwalitatieve aanlevering te faciliteren.<br />
        5.3 De aansprakelijkheid van het Platform is in elk geval beperkt tot het bedrag van de vergoedingen
        die de Zorgverlener in de twaalf maanden voorafgaand aan de schadeveroorzakende gebeurtenis via het
        Platform heeft ontvangen.</p>

        <p><strong>Artikel 6 — Toepasselijk recht en geschillen</strong><br />
        6.1 Deze overeenkomst wordt beheerst door het Belgisch recht.<br />
        6.2 Geschillen worden voorgelegd aan de bevoegde rechtbanken van het arrondissement waar de
        maatschappelijke zetel van het Platform is gevestigd.</p>

        <p><strong>Elektronische aanvaarding</strong><br />
        Door het aanvinken van de bevestigingsknop in de registratiewizard verklaart de Zorgverlener deze
        overeenkomst te hebben gelezen, begrepen en aanvaard. Deze elektronische aanvaarding heeft dezelfde
        rechtskracht als een handtekening conform de wet van 21 juli 2016 inzake elektronische handtekeningen.</p>

        <p className={styles.tosVersion}>Versie: {TOS_VERSION} — Datum: maart 2026</p>
      </div>

      <div className={styles.tosAgree}>
        <label className={styles.legalCheckbox}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
          />
          <span>
            Ik heb de gebruiksovereenkomst gelezen en ga hiermee akkoord.
          </span>
        </label>
        {error && <div className={styles.bannerError} role="alert">{error}</div>}
        <Button
          variant="primary"
          onClick={onAccept}
          disabled={!agreed || submitting}
        >
          {submitting ? 'Bezig…' : 'Aanvaarden en registratie voltooien'}
        </Button>
      </div>
    </div>
  );
}

interface CompletionStepProps {
  doctorName:    { firstName: string; lastName: string } | null;
  rizivNumber:   string | null;
  qualification: string | null;
  onDashboard:   () => void;
}

function CompletionStep({ doctorName, rizivNumber, qualification, onDashboard }: CompletionStepProps) {
  const fullName = doctorName
    ? `${doctorName.firstName} ${doctorName.lastName}`
    : null;

  return (
    <div className={styles.completionContainer}>
      <div className={styles.completionIcon} aria-hidden="true">
        <CheckIcon />
      </div>
      <h2 className={styles.completionTitle}>Verificatie voltooid</h2>
      {fullName && (
        <p className={styles.completionName}>Dr. {fullName}</p>
      )}
      <div className={styles.completionItems}>
        <div className={styles.completionItem}>
          <span className={styles.completionItemIcon}><CheckIcon /></span>
          <div>
            <p className={styles.completionItemLabel}>Identiteitskaart</p>
            <p className={styles.completionItemValue}>Geverifieerd via Belgische eID</p>
          </div>
        </div>
        <div className={styles.completionItem}>
          <span className={styles.completionItemIcon}><CheckIcon /></span>
          <div>
            <p className={styles.completionItemLabel}>RIZIV-registratie</p>
            {rizivNumber && (
              <p className={styles.completionItemValue}>
                {rizivNumber}
                {qualification && <> &mdash; {qualification}</>}
              </p>
            )}
          </div>
        </div>
      </div>
      <Button variant="primary" onClick={onDashboard}>
        Naar dashboard
      </Button>
    </div>
  );
}
